import React, { useState, useEffect, useRef, lazy, Suspense, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import Button from "../components/Button";
import { supabase } from "../supabase";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import { sendTeamInviteEmail, sendCandidateVerificationRequestEmail } from "../services/emailService";
import { getAdminsOptedInForVerificationEmails } from "../utils/adminUtils";
import { steps, REFERRAL_SOURCE_OPTIONS } from "../components/employee/onboarding/constants";

// Lazy load step components for better performance
const EmployeeOnboardingStep0 = lazy(() => import("../components/employee/onboarding/EmployeeOnboardingStep0"));
const EmployeeOnboardingStep1 = lazy(() => import("../components/employee/onboarding/EmployeeOnboardingStep1"));
const EmployeeOnboardingStep2 = lazy(() => import("../components/employee/onboarding/EmployeeOnboardingStep2"));
const EmployeeOnboardingStep3 = lazy(() => import("../components/employee/onboarding/EmployeeOnboardingStep3"));
const EmployeeOnboardingStep4 = lazy(() => import("../components/employee/onboarding/EmployeeOnboardingStep4"));

const EmployeeOnboardingPage = () => {
    const { user } = useAuth();
    const { profileInfo, updateProfileInfo } = useProfileInfo();
    const navigate = useNavigate();
    
    const employmentTypeOptions = [
        { value: "full-time", label: "Full-time" },
        { value: "part-time", label: "Part-time" },
        { value: "contract", label: "Contract" }
    ];
    
    const locationTypeOptions = [
        { value: "on-site", label: "On-site" },
        { value: "remote", label: "Remote" },
        { value: "hybrid", label: "Hybrid" }
    ];
    
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [roleInput, setRoleInput] = useState("");
    const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState([]);
    const [personalInfo, setPersonalInfo] = useState({
        firstName: "",
        lastName: "",
        headline: "",
        email: "",
        cellNumber: "",
        dateOfBirth: "",
        sex: "",
        linkedinUrl: "",
        jobSearchStatus: "",
        hpcsaRegNo: "",
        locationCityCountry: "",
        country: "",
        countryCode: "",
        provinceState: "",
        referralSource: "",
        uploadedFileName: "",
        profileImageUrl: "",
        profileImageFileName: "",
    });
    const [cvUploading, setCvUploading] = useState(false);
    const [profileImageUploading, setProfileImageUploading] = useState(false);
    const verificationRequestedRef = useRef(user?.user_metadata?.hpcsa_verification_requested === true);
    const hasLoadedDraft = useRef(false);
    
    // Refs for scrolling to newly added items (tablet only)
    const experienceRefs = useRef({});
    const educationRefs = useRef({});
    const certificationRefs = useRef({});
    const [isTablet, setIsTablet] = useState(false);
    
    // Check if tablet viewport
    useEffect(() => {
        const checkTablet = () => {
            const width = window.innerWidth;
            setIsTablet(width >= 768 && width < 1024);
        };
        checkTablet();
        window.addEventListener('resize', checkTablet);
        return () => window.removeEventListener('resize', checkTablet);
    }, []);
    
    const scrollToExperience = useCallback((idx) => {
        if (!isTablet) return;
        const ref = experienceRefs.current[idx];
        if (ref) {
            setTimeout(() => {
                ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [isTablet]);
    
    const addExperience = () => {
        const newIdx = experiences.length;
        setExperiences((prev) => [
            ...prev,
            { company: "", title: "", isCurrent: false, startDate: "", endDate: "", country: "", countryCode: "", provinceState: "", employmentType: "", locationType: "", description: "" },
        ]);
        scrollToExperience(newIdx);
    };

    const handleReferralSourceSelect = (value) => {
        if (value === "Other") {
            setIsCustomReferralSource(true);
            setPersonalInfo((p) => ({ ...p, referralSource: "" }));
            return;
        }

        setIsCustomReferralSource(false);
        setPersonalInfo((p) => ({ ...p, referralSource: value }));
    };

    const resetReferralSourceToSelect = () => {
        setIsCustomReferralSource(false);
        setPersonalInfo((p) => ({ ...p, referralSource: "" }));
    };
    const [completedSteps, setCompletedSteps] = useState([]); // indices marked as completed after Continue
    const [experiences, setExperiences] = useState([
        {
            company: "",
            title: "",
            isCurrent: false,
            startDate: "",
            endDate: "",
            country: "",
            countryCode: "",
            provinceState: "",
            employmentType: "",
            locationType: "",
            description: "",
        },
    ]);
    const [educations, setEducations] = useState([
        {
            school: "",
            fieldOfStudy: "",
            degree: "",
            grade: "",
            country: "",
            countryCode: "",
            provinceState: "",
            isCurrent: false,
            startDate: "",
            endDate: "",
            description: "",
        },
    ]);
    const [certifications, setCertifications] = useState([
        {
            courseName: "",
            issuingOrg: "",
            issueDate: "",
            expiryDate: "",
            credentialId: "",
            credentialUrl: "",
        },
    ]);
    const [isCustomReferralSource, setIsCustomReferralSource] = useState(false);
    // Skills step state
    const [skillInput, setSkillInput] = useState("");
    const [skills, setSkills] = useState([]); // { name: string, level: 'foundational'|'intermediate'|'advanced' }
  // States/Provinces will be fetched from country-state-city using countryCode
    const [languagesInput, setLanguagesInput] = useState("");
    const [languages, setLanguages] = useState([]);
    const [carLicense, setCarLicense] = useState(null); // true | false | null
    // Complete step state
    const [aboutText, setAboutText] = useState("");
    const [profileVisibility, setProfileVisibility] = useState(null); // 'public' | 'invisible-current' | 'hidden'
    const { showSuccess, showError } = useSnackbarNotification();
    const [errors, setErrors] = useState({});

    const hasAnyContent = (obj, keys) => {
        if (!obj) return false;
        return keys.some((key) => {
            const value = obj[key];
            if (typeof value === "string") {
                return value.trim().length > 0;
            }
            return Boolean(value);
        });
    };

    const filterEmptyEntries = (items, keys) => {
        if (!Array.isArray(items)) return [];
        return items.filter((item) => hasAnyContent(item, keys));
    };

    // Preload selections from user metadata
    useEffect(() => {
        const saved = user?.user_metadata?.profile_categories;
        if (Array.isArray(saved) && saved.length > 0) {
            setSelectedCategories(saved);
        }
        const savedRoles = user?.user_metadata?.profile_roles;
        if (Array.isArray(savedRoles) && savedRoles.length > 0) {
            setSelectedRoles(savedRoles);
        }
        const savedEmployment = user?.user_metadata?.profile_employment_types;
        if (Array.isArray(savedEmployment) && savedEmployment.length > 0) {
            setSelectedEmploymentTypes(savedEmployment);
        }
        // Prefill personal info from account settings metadata
        if (user?.user_metadata) {
            const md = user.user_metadata;
            const referralSourceFromMetadata = md.referral_source || "";
            setPersonalInfo((prev) => ({
                ...prev,
                firstName: md.first_name || "",
                lastName: md.last_name || "",
                headline: md.headline || "",
                email: md.email || user.email || "",
                cellNumber: md.cell_number || "",
                dateOfBirth: md.date_of_birth || "",
                sex: md.sex || "",
                linkedinUrl: md.linkedin_url || "",
                jobSearchStatus: md.job_search_status || "",
                hpcsaRegNo: md.hpcsa_reg_no || "",
                locationCityCountry: md.location_city_country || md.country || "",
                country: md.country || "",
                countryCode: md.country_code || "",
                provinceState: md.province_state || "",
                referralSource: referralSourceFromMetadata,
            }));
            setIsCustomReferralSource(
                Boolean(referralSourceFromMetadata) && !REFERRAL_SOURCE_OPTIONS.includes(referralSourceFromMetadata)
            );
            
            // Prefill collections
            if (Array.isArray(md.profile_experiences)) setExperiences(md.profile_experiences);
            if (Array.isArray(md.profile_educations)) setEducations(md.profile_educations);
            if (Array.isArray(md.profile_certifications)) setCertifications(md.profile_certifications);
            if (Array.isArray(md.profile_skills)) setSkills(md.profile_skills);
            if (Array.isArray(md.profile_languages)) setLanguages(md.profile_languages);
            if (typeof md.profile_car_license === 'boolean') setCarLicense(md.profile_car_license);
            if (typeof md.profile_about === 'string') setAboutText(md.profile_about);
            if (typeof md.profile_visibility === 'string') setProfileVisibility(md.profile_visibility);

            // Prefill from grouped object my_profile if present
            const grouped = md.my_profile || {};
            if (grouped.profile_role) {
                const pr = grouped.profile_role;
                if (Array.isArray(pr.categories)) setSelectedCategories(pr.categories);
                if (Array.isArray(pr.roles)) setSelectedRoles(pr.roles);
                if (Array.isArray(pr.employmentTypes)) setSelectedEmploymentTypes(pr.employmentTypes);
            }
            if (grouped.profile_experience) {
                const pe = grouped.profile_experience;
                if (Array.isArray(pe.experiences)) setExperiences(pe.experiences);
                if (Array.isArray(pe.educations)) setEducations(pe.educations);
                if (Array.isArray(pe.certifications)) setCertifications(pe.certifications);
            }
            if (grouped.profile_skills) {
                const ps = grouped.profile_skills;
                if (Array.isArray(ps.skills)) setSkills(ps.skills);
                if (Array.isArray(ps.languages)) setLanguages(ps.languages);
                if (typeof ps.carLicense === 'boolean') setCarLicense(ps.carLicense);
            }
            if (grouped.profile_complete) {
                const pc = grouped.profile_complete;
                if (typeof pc.about === 'string') setAboutText(pc.about);
                if (typeof pc.visibility === 'string') setProfileVisibility(pc.visibility);
            }
        }
    }, [user]);

    useEffect(() => {
        if (!profileInfo) return;

        setPersonalInfo((prev) => {
            const updates = {};
            const maybeHydrate = (key, value) => {
                if (value === null || value === undefined || value === "") return;
                const current = prev[key];
                const isEmpty =
                    typeof current === "string"
                        ? current.trim().length === 0
                        : current === null || current === undefined;
                if (isEmpty) {
                    updates[key] = value;
                }
            };

            maybeHydrate("firstName", profileInfo.firstname);
            maybeHydrate("lastName", profileInfo.lastname);
            maybeHydrate("email", profileInfo.email);
            maybeHydrate("cellNumber", profileInfo.cellnumber);
            maybeHydrate("dateOfBirth", profileInfo.dateofbirth);
            maybeHydrate("sex", profileInfo.sex);
            maybeHydrate("linkedinUrl", profileInfo.linkedinurl);
            maybeHydrate("jobSearchStatus", profileInfo.jobsearchstatus);
            maybeHydrate("hpcsaRegNo", profileInfo.hpcsa_reg_no);
            maybeHydrate("locationCityCountry", profileInfo.location_city_country);
            maybeHydrate("country", profileInfo.country);
            maybeHydrate("countryCode", profileInfo.country_code);
            maybeHydrate("provinceState", profileInfo.province_state);
            maybeHydrate("referralSource", profileInfo.referral_source);
            maybeHydrate("uploadedFileName", profileInfo.cv_filename);
            maybeHydrate("profileImageUrl", profileInfo.profile_image_url);

            if (Object.keys(updates).length === 0) {
                return prev;
            }

            return { ...prev, ...updates };
        });

        setAboutText((prev) => {
            if (typeof prev === "string" && prev.trim().length > 0) return prev;
            if (typeof profileInfo.profile_about === "string" && profileInfo.profile_about.trim().length > 0) {
                return profileInfo.profile_about;
            }
            return prev;
        });

        setProfileVisibility((prev) => {
            if (typeof prev === "string" && prev.length > 0) return prev;
            if (typeof profileInfo.profile_visibility === "string" && profileInfo.profile_visibility.length > 0) {
                return profileInfo.profile_visibility;
            }
            return prev;
        });

        setSkills((prev) => {
            if (Array.isArray(prev) && prev.length > 0) return prev;
            if (Array.isArray(profileInfo.skills)) return profileInfo.skills;
            try {
                if (typeof profileInfo.skills === "string") {
                    const parsed = JSON.parse(profileInfo.skills);
                    if (Array.isArray(parsed)) return parsed;
                }
            } catch (err) {
                console.warn("Failed to parse profile skills", err);
            }
            return prev;
        });

        setLanguages((prev) => {
            if (Array.isArray(prev) && prev.length > 0) return prev;
            if (Array.isArray(profileInfo.languages)) return profileInfo.languages;
            try {
                if (typeof profileInfo.languages === "string") {
                    const parsed = JSON.parse(profileInfo.languages);
                    if (Array.isArray(parsed)) return parsed;
                }
            } catch (err) {
                console.warn("Failed to parse profile languages", err);
            }
            return prev;
        });

        setCarLicense((prev) => {
            if (prev === true || prev === false) return prev;
            if (typeof profileInfo.car_license === "boolean") {
                return profileInfo.car_license;
            }
            return prev;
        });
    }, [profileInfo]);

    // Persist in-progress onboarding data locally so users don't lose work
    // Only load on initial mount to prevent resetting currentStep when profileInfo changes
    useEffect(() => {
        if (!user || typeof window === "undefined" || hasLoadedDraft.current) return;
        const storageKey = `employee_onboarding_draft_${user.id}`;
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) {
                hasLoadedDraft.current = true;
                return;
            }
            const saved = JSON.parse(raw);

            // Don't load currentStep from saved state - let user continue from current step
            // This prevents resetting the step when profileInfo updates after saving
            // if (typeof saved.currentStep === "number") setCurrentStep(saved.currentStep);
            if (Array.isArray(saved.completedSteps)) setCompletedSteps(saved.completedSteps);
            if (Array.isArray(saved.selectedCategories)) setSelectedCategories(saved.selectedCategories);
            if (Array.isArray(saved.selectedRoles)) setSelectedRoles(saved.selectedRoles);
            if (typeof saved.roleInput === "string") setRoleInput(saved.roleInput);
            if (Array.isArray(saved.selectedEmploymentTypes)) setSelectedEmploymentTypes(saved.selectedEmploymentTypes);
            if (saved.personalInfo && typeof saved.personalInfo === "object") {
                setPersonalInfo((prev) => ({ ...prev, ...saved.personalInfo }));
            }
            if (Array.isArray(saved.experiences)) setExperiences(saved.experiences);
            if (Array.isArray(saved.educations)) setEducations(saved.educations);
            if (Array.isArray(saved.certifications)) setCertifications(saved.certifications);
            if (typeof saved.isCustomReferralSource === "boolean") setIsCustomReferralSource(saved.isCustomReferralSource);
            if (typeof saved.skillInput === "string") setSkillInput(saved.skillInput);
            if (Array.isArray(saved.skills)) setSkills(saved.skills);
            if (typeof saved.languagesInput === "string") setLanguagesInput(saved.languagesInput);
            if (Array.isArray(saved.languages)) setLanguages(saved.languages);
            if (saved.carLicense === true || saved.carLicense === false || saved.carLicense === null) {
                setCarLicense(saved.carLicense);
            }
            if (typeof saved.aboutText === "string") setAboutText(saved.aboutText);
            if (typeof saved.profileVisibility === "string") setProfileVisibility(saved.profileVisibility);
            
            hasLoadedDraft.current = true;
        } catch (err) {
            console.warn("Failed to load onboarding draft from localStorage", err);
            hasLoadedDraft.current = true;
        }
    }, [user]); // Removed profileInfo dependency to prevent resetting currentStep

    useEffect(() => {
        if (!user || typeof window === "undefined") return;
        const storageKey = `employee_onboarding_draft_${user.id}`;
        const payload = {
            currentStep,
            completedSteps,
            selectedCategories,
            selectedRoles,
            roleInput,
            selectedEmploymentTypes,
            personalInfo,
            experiences,
            educations,
            certifications,
            isCustomReferralSource,
            skillInput,
            skills,
            languagesInput,
            languages,
            carLicense,
            aboutText,
            profileVisibility,
        };
        try {
            localStorage.setItem(storageKey, JSON.stringify(payload));
        } catch (err) {
            console.warn("Failed to save onboarding draft to localStorage", err);
        }
    }, [
        user,
        currentStep,
        completedSteps,
        selectedCategories,
        selectedRoles,
        roleInput,
        selectedEmploymentTypes,
        personalInfo,
        experiences,
        educations,
        certifications,
        isCustomReferralSource,
        skillInput,
        skills,
        languagesInput,
        languages,
        carLicense,
        aboutText,
        profileVisibility,
    ]);

    const handleCVUpload = async (file) => {
        if (!file || !user) return;
        try {
            setCvUploading(true);
            // Store in a dedicated bucket and folder per user
            // Use env-configured bucket or fall back to your new bucket name
            const bucket = process.env.REACT_APP_CV_BUCKET || "my-cv-uploads";
            const filePath = `${user.id}/${Date.now()}_${file.name}`;
            const { error: uploadError } = await supabase.storage.from(bucket).upload(filePath, file, {
                upsert: true,
                contentType: file.type || "application/octet-stream",
            });
            if (uploadError) throw uploadError;

            // Get public URL
            const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(filePath);
            const publicUrl = publicData?.publicUrl || "";

            // Save reference in user metadata
            const { error: metaError } = await supabase.auth.updateUser({
                data: {
                    profile_cv_url: publicUrl,
                    profile_cv_path: `${bucket}/${filePath}`,
                    profile_cv_filename: file.name,
                },
            });
            if (metaError) throw metaError;

            setPersonalInfo((p) => ({ ...p, uploadedFileName: file.name }));
            showSuccess("CV uploaded successfully");
        } catch (e) {
            console.error("CV upload error", e);
            showError(e.message || "Failed to upload CV");
        } finally {
            setCvUploading(false);
        }
    };

    const handleProfileImageUpload = async (file) => {
        if (!file || !user) return;
        try {
            setProfileImageUploading(true);
            // Store in profile images bucket
            const bucket = process.env.REACT_APP_PROFILE_IMAGES_BUCKET || "profile-images";
            const filePath = `${user.id}/${Date.now()}_${file.name}`;
            const { error: uploadError } = await supabase.storage.from(bucket).upload(filePath, file, {
                upsert: true,
                contentType: file.type || "image/jpeg",
            });
            if (uploadError) throw uploadError;

            // Get public URL
            const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(filePath);
            const publicUrl = publicData?.publicUrl || "";

            // Save reference in user metadata
            const { error: metaError } = await supabase.auth.updateUser({
                data: {
                    profile_image_url: publicUrl,
                    profile_image_path: `${bucket}/${filePath}`,
                    profile_image_filename: file.name,
                },
            });
            if (metaError) throw metaError;

            // Also update users_profiles table (same as MyProfile page)
            const { error: profileError } = await supabase
                .from('users_profiles')
                .update({ profile_image_url: publicUrl })
                .eq('profileid', user.id);
            
            if (profileError) {
                console.warn('Failed to update users_profiles table:', profileError);
                // Don't throw here, as the metadata update succeeded
            }

            setPersonalInfo((p) => ({ 
                ...p, 
                profileImageUrl: publicUrl,
                profileImageFileName: file.name 
            }));
            showSuccess("Profile image uploaded successfully");
        } catch (e) {
            console.error("Profile image upload error", e);
            showError(e.message || "Failed to upload profile image");
        } finally {
            setProfileImageUploading(false);
        }
    };

    // Intercept browser back to step through onboarding instead of leaving
    const stepRef = useRef(currentStep);
    useEffect(() => {
        stepRef.current = currentStep;
    }, [currentStep]);
    useEffect(() => {
        const onPopState = async (e) => {
            e.preventDefault();
            if (stepRef.current === 0) {
                // Sign out user before redirecting to login
                await supabase.auth.signOut();
                navigate("/login", { replace: true });
            } else {
                setCurrentStep((s) => Math.max(0, s - 1));
                window.history.pushState(null, "", window.location.href);
            }
        };
        // Push a state so the first back is handled by our listener
        window.history.pushState(null, "", window.location.href);
        window.addEventListener("popstate", onPopState);
        return () => {
            window.removeEventListener("popstate", onPopState);
        };
    }, [navigate]);

    // Redirect to dashboard if onboarding is already completed
    // Database uses lowercase 'onboardingcompleted'
    useEffect(() => {
        if (profileInfo?.onboardingcompleted === true) {
            navigate("/employee/dashboard", { replace: true });
        }
    }, [profileInfo?.onboardingcompleted, navigate]);

    if (!user) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-black">
                    Please log in or sign up to view your profile
                </div>
            </div>
        );
    }

    const goBack = async () => {
        if (currentStep === 0) {
            // Sign out user before redirecting to login
            await supabase.auth.signOut();
            navigate("/login", { replace: true });
            return;
        }
        setCurrentStep((s) => Math.max(0, s - 1));
    };

    const handleSkip = async () => {
        // If on the last step, complete onboarding
        if (currentStep === steps.length - 1) {
            try {
                // Mark onboarding as completed
                await updateProfileInfo({
                    onboardingCompleted: true,
                    onboardingStep: steps.length - 1,
                });

                // Clear any saved local draft once onboarding is fully completed
                if (typeof window !== "undefined" && user) {
                    const storageKey = `employee_onboarding_draft_${user.id}`;
                    try {
                        localStorage.removeItem(storageKey);
                    } catch (err) {
                        console.warn("Failed to clear onboarding draft from localStorage", err);
                    }
                }

                navigate("/employee/dashboard");
            } catch (error) {
                console.error("Error completing onboarding:", error);
                showError("Failed to complete onboarding. Please try again.");
            }
            return;
        }
        // Otherwise, move to the next step
        setCurrentStep((s) => Math.min(steps.length - 1, s + 1));
    };

    // Validate each step before allowing the user to proceed
    const validateCurrentStep = () => {
        if (currentStep === 0) {
            const hasCategories = selectedCategories.length > 0;
            const hasRoles = selectedRoles.length > 0;
            const hasEmploymentTypes = selectedEmploymentTypes.length > 0;
            const nextErrors = {
                categories: !hasCategories,
                roles: !hasRoles,
                employmentTypes: !hasEmploymentTypes,
            };
            setErrors((prev) => ({ ...prev, ...nextErrors }));
            return hasCategories && hasRoles && hasEmploymentTypes;
        }
        if (currentStep === 1) {
            const requiredPersonal = [
                personalInfo.firstName,
                personalInfo.lastName,
                personalInfo.headline,
                personalInfo.hpcsaRegNo,
            ];
            const hasAll = requiredPersonal.every((v) => typeof v === "string" && v.trim().length > 0);
            const nextErrors = {
                firstName: !(personalInfo.firstName || '').trim(),
                lastName: !(personalInfo.lastName || '').trim(),
                headline: !(personalInfo.headline || '').trim(),
                hpcsaRegNo: !(personalInfo.hpcsaRegNo || '').trim(),
                email: false,
                dateOfBirth: false,
                sex: false,
                jobSearchStatus: false,
                locationCityCountry: false,
                cellNumber: false,
                linkedinUrl: false,
            };
            setErrors((prev) => ({ ...prev, ...nextErrors }));
            return hasAll;
        }
        if (currentStep === 2) {
            // Step 2 (Experience/Education) is now completely optional
            return true;
        }
        if (currentStep === 3) {
            // Step 4 (Skills) is now completely optional - no validation required
            return true;
        }
        if (currentStep === 4) {
            // More lenient validation - only require profileVisibility (aboutText is optional)
            const hasVisibility = profileVisibility !== null && profileVisibility !== undefined && 
                                 (typeof profileVisibility === "string" && profileVisibility.length > 0);
            return hasVisibility;
        }
        return true;
    };

    const goNext = async () => {
        // Block progression if required fields are missing
        if (!validateCurrentStep()) {
            showError("Please complete all required fields before continuing.");
            return;
        }

        try {
            // Persist Step 1 selections before moving forward
            if (currentStep === 0) {
                // GROUPED-ONLY for role
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_role: {
                        categories: selectedCategories,
                        roles: selectedRoles,
                        employmentTypes: selectedEmploymentTypes,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: {
                        my_profile: myProfile,
                    },
                });
                if (error) throw error;
                showSuccess("Preferences saved");
            } else if (currentStep === 1) {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_personal_info: {
                        // We still keep main auth fields flat, but mirror helpful ones
                        headline: personalInfo.headline,
                        linkedinUrl: personalInfo.linkedinUrl,
                        jobSearchStatus: personalInfo.jobSearchStatus,
                        hpcsaRegNo: personalInfo.hpcsaRegNo,
                        locationCityCountry: personalInfo.locationCityCountry,
                        country: personalInfo.country,
                        country_code: personalInfo.countryCode,
                        provinceState: personalInfo.provinceState,
                        referralSource: personalInfo.referralSource,
                        cv: {
                            url: user?.user_metadata?.profile_cv_url || null,
                            path: user?.user_metadata?.profile_cv_path || null,
                            filename: user?.user_metadata?.profile_cv_filename || null,
                        },
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: {
                        first_name: personalInfo.firstName,
                        last_name: personalInfo.lastName,
                        headline: personalInfo.headline,
                        email: personalInfo.email,
                        cell_number: personalInfo.cellNumber,
                        date_of_birth: personalInfo.dateOfBirth,
                        sex: personalInfo.sex,
                        linkedin_url: personalInfo.linkedinUrl,
                        job_search_status: personalInfo.jobSearchStatus,
                        hpcsa_reg_no: personalInfo.hpcsaRegNo,
                        location_city_country: personalInfo.locationCityCountry,
                        country: personalInfo.country,
                        country_code: personalInfo.countryCode,
                        province_state: personalInfo.provinceState,
                        referral_source: personalInfo.referralSource,
                        my_profile: myProfile,
                    },
                });
                if (error) throw error;

                // Mirror key fields to users_profiles for querying/analytics
                const profileUpdate = {
                    profileid: user.id,
                    email: user.email,
                    firstname: personalInfo.firstName || null,
                    lastname: personalInfo.lastName || null,
                    headline: personalInfo.headline || null,
                    country: personalInfo.country || null,
                    country_code: personalInfo.countryCode || null,
                    province_state: personalInfo.provinceState || null,
                    referral_source: personalInfo.referralSource || null,
                    location_city_country: personalInfo.locationCityCountry || null,
                    cellnumber: personalInfo.cellNumber || null,
                    dateofbirth: personalInfo.dateOfBirth || null,
                    sex: personalInfo.sex || null,
                    linkedinurl: personalInfo.linkedinUrl || null,
                    jobsearchstatus: personalInfo.jobSearchStatus || null,
                };
                
                // Add HPCSA number if column exists (for admin verification dashboard)
                // Note: This requires adding hpcsa_reg_no column to users_profiles table
                if (personalInfo.hpcsaRegNo) {
                    profileUpdate.hpcsa_reg_no = personalInfo.hpcsaRegNo;
                }
                
                const { error: profileError } = await supabase
                    .from('users_profiles')
                    .upsert(profileUpdate, { onConflict: 'profileid' });
                if (profileError) {
                    // If hpcsa_reg_no column doesn't exist, try without it
                    if (profileError.code === '42703' && personalInfo.hpcsaRegNo) {
                        delete profileUpdate.hpcsa_reg_no;
                        const { error: retryError } = await supabase
                            .from('users_profiles')
                            .upsert(profileUpdate, { onConflict: 'profileid' });
                        if (retryError) throw retryError;
                    } else {
                        throw profileError;
                    }
                }
                showSuccess("Personal info saved");

                const trimmedHpcsa = typeof personalInfo.hpcsaRegNo === "string" ? personalInfo.hpcsaRegNo.trim() : "";
                const alreadyVerified = user?.user_metadata?.hpcsa_verified === true;
                const alreadyRequested = verificationRequestedRef.current || user?.user_metadata?.hpcsa_verification_requested === true;
                const localVerificationKey = user ? `hpcsa_verification_requested_${user.id}` : null;
                const localRequested =
                    typeof window !== "undefined" && localVerificationKey
                        ? localStorage.getItem(localVerificationKey) === "true"
                        : false;

                if (trimmedHpcsa && !alreadyVerified && !alreadyRequested && !localRequested) {
                    try {
                        const candidateName =
                            `${personalInfo.firstName || ""} ${personalInfo.lastName || ""}`.trim() ||
                            user.email ||
                            "Candidate";
                        // Send admins to the main HPCSA verification dashboard
                        const reviewLink = "https://doctorsjobs.io/admin/hpcsa-verification";

                        // Get admins who have opted in to receive verification emails
                        const adminEmails = await getAdminsOptedInForVerificationEmails(supabase);
                        
                        if (adminEmails.length > 0) {
                            await Promise.all(
                                adminEmails.map((adminEmail) =>
                                    sendCandidateVerificationRequestEmail(adminEmail, candidateName, reviewLink)
                                )
                            );
                        }

                        verificationRequestedRef.current = true;
                        if (typeof window !== "undefined" && localVerificationKey) {
                            localStorage.setItem(localVerificationKey, "true");
                        }

                        await supabase.auth.updateUser({
                            data: {
                                hpcsa_verification_requested: true,
                                hpcsa_verification_requested_at: new Date().toISOString()
                            }
                        });
                    } catch (notifyError) {
                        console.error("Failed to send verification notification email:", notifyError);
                        if (typeof window !== "undefined" && localVerificationKey) {
                            localStorage.removeItem(localVerificationKey);
                        }
                    }
                }
            } else if (currentStep === 2) {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_experience: {
                        experiences,
                        educations,
                        certifications,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: {
                        my_profile: myProfile,
                    },
                });
                if (error) throw error;
                showSuccess("Experience saved");
            } else if (currentStep === 3) {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_skills: {
                        skills,
                        languages,
                        carLicense,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: {
                        my_profile: myProfile,
                    },
                });
                if (error) throw error;
                showSuccess("Skills saved");
            } else if (currentStep === 4) {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_complete: {
                        about: aboutText,
                        visibility: profileVisibility,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: {
                        my_profile: myProfile,
                    },
                });
                if (error) throw error;

                const filteredExperiences = filterEmptyEntries(experiences, ["company", "title", "description", "startDate", "endDate", "country", "countryCode", "provinceState", "employmentType", "locationType"]);
                const filteredEducations = filterEmptyEntries(educations, ["school", "fieldOfStudy", "degree", "description", "startDate", "endDate", "country", "countryCode", "provinceState"]);
                const filteredCertifications = filterEmptyEntries(certifications, ["courseName", "issuingOrg", "credentialId"]);
                const filteredSkills = Array.isArray(skills)
                    ? skills.filter((skill) => typeof skill?.name === "string" && skill.name.trim().length > 0)
                    : [];
                const filteredLanguages = Array.isArray(languages)
                    ? languages.filter((lng) => typeof lng === "string" && lng.trim().length > 0)
                    : [];

                // Save categories and visibility to database for profile display
                const { error: dbError } = await supabase
                    .from('users_profiles')
                    .update({ 
                        selected_categories: JSON.stringify(selectedCategories || []),
                        selected_roles: JSON.stringify(selectedRoles || []),
                        employment_types: JSON.stringify(selectedEmploymentTypes || []),
                        profile_about: aboutText || null,
                        profile_visibility: profileVisibility,
                        skills: JSON.stringify(filteredSkills),
                        languages: JSON.stringify(filteredLanguages),
                        car_license: carLicense,
                        work_experiences: JSON.stringify(filteredExperiences),
                        educations: JSON.stringify(filteredEducations),
                        certifications: JSON.stringify(filteredCertifications),
                    })
                    .eq('profileid', user.id);  

                if (dbError) throw dbError;

                // Update the database profile to mark onboarding as completed
                await updateProfileInfo({
                    onboardingCompleted: true,
                    onboardingStep: 4,
                    profileVisibility: profileVisibility
                });

                // Clear any saved local draft once onboarding is fully completed
                if (typeof window !== "undefined" && user) {
                    const storageKey = `employee_onboarding_draft_${user.id}`;
                    try {
                        localStorage.removeItem(storageKey);
                    } catch (err) {
                        console.warn("Failed to clear onboarding draft from localStorage", err);
                    }
                }

                showSuccess("Profile completed");
                navigate("/employee/dashboard");
            }

            // Only mark step complete and advance after successful save
            // Skip advancement for step 4 (last step) as it navigates away
            if (currentStep !== 4) {
                setCompletedSteps((prev) => (prev.includes(currentStep) ? prev : [...prev, currentStep]));
                setCurrentStep((s) => Math.min(steps.length - 1, s + 1));
            }
        } catch (e) {
            showError(e.message || "Failed to save preferences");
        }
    };

    return (
        <>
        <style>{`
            /* Mobile-only styles - Desktop remains unchanged */
            @media (max-width: 767px) {
                .employee-onboarding-container {
                    padding: 0.25rem !important;
                    padding-top: 0.5rem !important;
                    padding-bottom: 0.5rem !important;
                }
                .employee-onboarding-main-content {
                    padding: 0.75rem !important;
                }
                .employee-onboarding-main-content h1 {
                    font-size: 1.125rem !important;
                    margin-bottom: 0.75rem !important;
                    line-height: 1.5rem !important;
                }
                .employee-onboarding-step-indicator {
                    font-size: 0.75rem !important;
                }
                .employee-onboarding-category-dropdown {
                    margin-bottom: 0.75rem !important;
                }
                .employee-onboarding-category-dropdown select {
                    font-size: 0.875rem !important;
                    padding: 0.625rem 0.875rem !important;
                    min-height: 44px !important;
                }
                .employee-onboarding-category-pills {
                    gap: 0.5rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .employee-onboarding-category-pills span {
                    font-size: 0.75rem !important;
                    padding: 0.375rem 0.625rem !important;
                }
                .employee-onboarding-form-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
                .employee-onboarding-form-grid input,
                .employee-onboarding-form-grid select {
                    font-size: 0.875rem !important;
                    padding: 0.625rem 0.875rem !important;
                    min-height: 44px !important;
                }
                .employee-onboarding-upload-section {
                    flex-direction: column !important;
                    gap: 1.5rem !important;
                }
                .employee-onboarding-upload-section > div {
                    width: 100% !important;
                }
                .employee-onboarding-button {
                    font-size: 0.875rem !important;
                    padding: 0.625rem 1.25rem !important;
                    min-height: 44px !important;
                }
                .employee-onboarding-navigation {
                    flex-direction: column-reverse !important;
                    gap: 0.625rem !important;
                    margin-top: 1.25rem !important;
                }
                .employee-onboarding-navigation button {
                    width: 100% !important;
                    min-height: 44px !important;
                }
                /* Header actions - stack on mobile */
                .employee-onboarding-header-actions {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 1rem !important;
                }
                .employee-onboarding-header-actions h1 {
                    width: 100% !important;
                }
                .employee-onboarding-header-actions button {
                    width: 100% !important;
                    min-height: 44px !important;
                    font-size: 0.875rem !important;
                    padding: 0.625rem 0.875rem !important;
                }
                /* Section headers - stack on mobile */
                .employee-onboarding-section-header {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.75rem !important;
                }
                .employee-onboarding-section-header h2 {
                    font-size: 1.25rem !important;
                    width: 100% !important;
                }
                .employee-onboarding-section-header button {
                    width: 100% !important;
                    min-height: 44px !important;
                    font-size: 0.875rem !important;
                    padding: 0.625rem 0.875rem !important;
                }
                /* React Select mobile fixes - ensure dropdowns are accessible */
                .react-select__control {
                    min-height: 44px !important;
                    font-size: 0.875rem !important;
                }
                .react-select__value-container {
                    padding: 0.375rem 0.625rem !important;
                }
                .react-select__input-container {
                    margin: 0 !important;
                }
                .react-select__menu {
                    font-size: 0.875rem !important;
                    z-index: 9999 !important;
                }
                .react-select__menu-portal {
                    z-index: 9999 !important;
                }
                /* Skills matrix - scrollable on mobile */
                .employee-onboarding-skills-matrix {
                    overflow-x: auto !important;
                    -webkit-overflow-scrolling: touch !important;
                    margin: 0 -0.75rem !important;
                    padding: 0 0.75rem !important;
                }
                .employee-onboarding-skills-matrix .grid-cols-4 {
                    min-width: 500px !important;
                }
                /* Ensure all interactive elements are touch-friendly */
                button, 
                input[type="submit"],
                label[for] {
                    min-height: 44px !important;
                }
                input[type="checkbox"],
                input[type="radio"] {
                    width: 20px !important;
                    height: 20px !important;
                    min-width: 20px !important;
                    min-height: 20px !important;
                }
                textarea {
                    font-size: 0.875rem !important;
                    padding: 0.625rem !important;
                    min-height: 100px !important;
                }
                /* Fix spacing between form sections */
                .employee-onboarding-form-section {
                    margin-bottom: 1.25rem !important;
                }
            }
            /* Tablet styles - increased padding */
            @media (min-width: 768px) and (max-width: 1023px) {
                .employee-onboarding-container {
                    padding: 1rem !important;
                    padding-top: 1.5rem !important;
                    padding-bottom: 1.5rem !important;
                }
                .employee-onboarding-main-content {
                    padding: 1.5rem !important;
                }
                .employee-onboarding-form-grid {
                    gap: 1rem !important;
                }
                .employee-onboarding-form-grid input,
                .employee-onboarding-form-grid select {
                    padding: 0.75rem 1rem !important;
                }
                .employee-onboarding-button {
                    padding: 0.75rem 1.5rem !important;
                }
                .employee-onboarding-header-actions button {
                    padding: 0.75rem 1rem !important;
                }
                .employee-onboarding-section-header button {
                    padding: 0.75rem 1rem !important;
                }
                .employee-onboarding-category-dropdown select {
                    padding: 0.75rem 1rem !important;
                }
                /* Make React-Select same size as regular inputs */
                .react-select__control {
                    min-height: 44px !important;
                    max-height: 44px !important;
                    height: 44px !important;
                }
                .react-select__value-container {
                    padding: 0 1rem !important;
                    height: 100% !important;
                }
                .react-select__input-container {
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .react-select__indicators {
                    height: 100% !important;
                }
                textarea {
                    padding: 0.75rem !important;
                }
                .employee-onboarding-form-section {
                    margin-bottom: 1.5rem !important;
                }
                /* Reduce padding on category pills for tablet */
                .category-pill {
                    padding: 5px 10px !important;
                    font-size: 13px !important;
                }
                /* Reduce padding on role pills for tablet */
                .employee-onboarding-role-pill {
                    padding-top: 2px !important;
                    padding-bottom: 2px !important;
                    padding-left: 8px !important;
                    padding-right: 8px !important;
                    gap: 4px !important;
                }
                /* Make all dropdowns/selects white background on tablet */
                .employee-onboarding-form-grid select,
                .employee-onboarding-main-content select {
                    background-color: white !important;
                }
                /* Fix date input visibility on tablet */
                .employee-onboarding-form-grid input[type="date"],
                .employee-onboarding-main-content input[type="date"] {
                    background-color: white !important;
                    color: #000000 !important;
                    opacity: 1 !important;
                }
                .employee-onboarding-form-grid input[type="date"]::-webkit-datetime-edit,
                .employee-onboarding-main-content input[type="date"]::-webkit-datetime-edit {
                    color: #000000 !important;
                    opacity: 1 !important;
                }
                .employee-onboarding-form-grid input[type="date"]::-webkit-datetime-edit-fields-wrapper,
                .employee-onboarding-main-content input[type="date"]::-webkit-datetime-edit-fields-wrapper {
                    color: #000000 !important;
                    opacity: 1 !important;
                }
                .employee-onboarding-form-grid input[type="date"]::-webkit-datetime-edit-text,
                .employee-onboarding-form-grid input[type="date"]::-webkit-datetime-edit-month-field,
                .employee-onboarding-form-grid input[type="date"]::-webkit-datetime-edit-day-field,
                .employee-onboarding-form-grid input[type="date"]::-webkit-datetime-edit-year-field,
                .employee-onboarding-main-content input[type="date"]::-webkit-datetime-edit-text,
                .employee-onboarding-main-content input[type="date"]::-webkit-datetime-edit-month-field,
                .employee-onboarding-main-content input[type="date"]::-webkit-datetime-edit-day-field,
                .employee-onboarding-main-content input[type="date"]::-webkit-datetime-edit-year-field {
                    color: #000000 !important;
                    opacity: 1 !important;
                }
                .employee-onboarding-form-grid input[type="date"]::-webkit-calendar-picker-indicator,
                .employee-onboarding-main-content input[type="date"]::-webkit-calendar-picker-indicator {
                    opacity: 1 !important;
                    filter: none !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-[#f3f4f6] flex items-start justify-center py-2 px-2 md:py-4 md:px-3 lg:py-6 lg:px-4 employee-onboarding-container">
            <div className="w-full max-w-6xl rounded-2xl mx-auto px-2 md:px-3 lg:px-0">
                <div className="flex flex-col xl:flex-row gap-4">
                    {/* Dropdown for steps on mobile & tablet (<1280px) */}
                    <div className="xl:hidden w-full mb-3">
                        <label className="block text-sm text-gray-700 mb-2">My Profile</label>
                        <select
                            className="w-full rounded-full border border-gray-300 px-4 py-2"
                            value={currentStep}
                            onChange={(e) => setCurrentStep(parseInt(e.target.value, 10))}
                        >
                            {steps.map((label, idx) => (
                                <option key={label} value={idx}>{`${idx + 1}. ${label}`}</option>
                            ))}
                        </select>
                    </div>

                    {/* Sidebar Steps - only on desktop (≥1280px) */}
                    <aside className="hidden xl:block xl:w-72 bg-white rounded-2xl shadow-sm border border-gray-200 p-4 h-max xl:mr-4">
                        <h3 className="text-gray-900 font-semibold mb-4">My Profile</h3>
                        <ol className="space-y-4">
                            {steps.map((label, idx) => {
                                const isCurrent = idx === currentStep;
                                return (
                                    <li key={label} className="flex items-center gap-3 cursor-pointer" onClick={() => setCurrentStep(idx)}>
                                        <span className={`${isCurrent ? "font-semibold text-black" : "text-gray-500"}`}>{idx + 1}.</span>
                                        <span className={`${isCurrent ? "text-black font-medium" : "text-gray-600"}`}>
                                            {label}
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>
                    </aside>

                    {/* Main content */}
                    <section className="flex-1 p-0">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 md:p-5 lg:p-6 xl:p-8 employee-onboarding-main-content">
                        <div className="flex items-center justify-between mb-3 text-gray-900">
                            <div className="flex items-center gap-2 employee-onboarding-step-indicator">
                                <button aria-label="Go back" onClick={goBack} className="p-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                                    </svg>
                                </button>
                                <span className="text-sm font-medium">Step {currentStep + 1} of {steps.length}</span>
                            </div>
                            <button
                                onClick={handleSkip}
                                className="text-sm text-black underline hover:text-gray-700"
                            >
                                Skip for now
                            </button>
                        </div>
                        <div className="flex items-center justify-between mb-3 sm:mb-4 employee-onboarding-header-actions">
                            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
                                {currentStep === 0 && "What roles are you looking for?"}
                                {currentStep === 1 && "Personal Info"}
                                {currentStep === 2 && "Experience"}
                                {currentStep === 3 && "Skills"}
                                {currentStep === 4 && "Complete"}
                            </h1>
                            {currentStep === 2 && (
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-full border-2 text-sm border-gray-300"
                                    onClick={addExperience}
                                >
                                    + Add Experience
                                </button>
                            )}
                        </div>

                        {/* Step content */}
                        <div className="bg-white rounded-xl px-0 pt-4 pb-2">
                            <Suspense fallback={
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                </div>
                            }>
                                {currentStep === 0 && (
                                    <EmployeeOnboardingStep0
                                        selectedCategories={selectedCategories}
                                        setSelectedCategories={setSelectedCategories}
                                        selectedRoles={selectedRoles}
                                        setSelectedRoles={setSelectedRoles}
                                        roleInput={roleInput}
                                        setRoleInput={setRoleInput}
                                        selectedEmploymentTypes={selectedEmploymentTypes}
                                        setSelectedEmploymentTypes={setSelectedEmploymentTypes}
                                        errors={errors}
                                    />
                                )}

                                {currentStep === 1 && (
                                    <EmployeeOnboardingStep1
                                        personalInfo={personalInfo}
                                        setPersonalInfo={setPersonalInfo}
                                        cvUploading={cvUploading}
                                        profileImageUploading={profileImageUploading}
                                        handleCVUpload={handleCVUpload}
                                        handleProfileImageUpload={handleProfileImageUpload}
                                        isCustomReferralSource={isCustomReferralSource}
                                        handleReferralSourceSelect={handleReferralSourceSelect}
                                        resetReferralSourceToSelect={resetReferralSourceToSelect}
                                        errors={errors}
                                    />
                                )}

                                {currentStep === 2 && (
                                    <EmployeeOnboardingStep2
                                        experiences={experiences}
                                        setExperiences={setExperiences}
                                        educations={educations}
                                        setEducations={setEducations}
                                        certifications={certifications}
                                        setCertifications={setCertifications}
                                        experienceRefs={experienceRefs}
                                        educationRefs={educationRefs}
                                        certificationRefs={certificationRefs}
                                    />
                                )}

                                {currentStep === 3 && (
                                    <EmployeeOnboardingStep3
                                        skillInput={skillInput}
                                        setSkillInput={setSkillInput}
                                        skills={skills}
                                        setSkills={setSkills}
                                        languagesInput={languagesInput}
                                        setLanguagesInput={setLanguagesInput}
                                        languages={languages}
                                        setLanguages={setLanguages}
                                        carLicense={carLicense}
                                        setCarLicense={setCarLicense}
                                    />
                                )}

                                {currentStep === 4 && (
                                    <EmployeeOnboardingStep4
                                        aboutText={aboutText}
                                        setAboutText={setAboutText}
                                        profileVisibility={profileVisibility}
                                        setProfileVisibility={setProfileVisibility}
                                    />
                                )}
                            </Suspense>
                        </div>

                        {/* Navigation buttons */}
                        <div className="flex justify-between mt-6 employee-onboarding-navigation">
                            <Button variant="text" onClick={goBack} className="!px-5 !py-2 employee-onboarding-button">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-1 w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                                </svg>
                                Back
                            </Button>
                            <Button onClick={goNext} className="!px-6 !py-2 employee-onboarding-button">
                                {currentStep === steps.length - 1 ? "Save & Complete Profile" : "Continue"}
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ml-2 w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                            </Button>
                        </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
        </>
    );
};

export default EmployeeOnboardingPage;


